import React, { useState } from "react";
import { Link } from "react-router-dom";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AppleIcon from "@mui/icons-material/Apple";
import VerifiedIcon from "@mui/icons-material/Verified";
import AddressEntryForm from "../AddressEntryForm";
import { getCampaignBillTypeLabel } from "../../utils/campaignApi";
import { formatDateWithTimezone } from "../../utils/campaignDateUtils";

const IOS_APP_STORE_URL = "https://apps.apple.com/us/app/civixthread/id6756985264";
const VERIFIED_GOLD = "#8B6914";

function getOrganizerInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "O";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function OrganizerCard({
  name,
  profileId,
  profileImageUrl,
  isVerified = false,
  preview = false,
  isLoggedIn = false,
  currentUserId = null,
  isFollowing = false,
  followLoading = false,
  onFollowToggle,
  onFollowAuthRequired,
}) {
  const [imageError, setImageError] = useState(false);
  const displayName = (name || "").trim();
  if (!displayName) return null;

  const isOwnProfile =
    profileId && currentUserId && String(profileId) === String(currentUserId);
  const canViewProfile = Boolean(profileId);
  const canFollow = !preview && canViewProfile && !isOwnProfile;
  const showImage = profileImageUrl && !imageError;

  const profileContent = (
    <>
      {showImage ? (
        <img
          src={profileImageUrl}
          alt=""
          className="take-action__organizer-card-avatar"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="take-action__organizer-card-avatar take-action__organizer-card-avatar--placeholder">
          {getOrganizerInitials(displayName)}
        </div>
      )}
      <div className="take-action__organizer-card-text">
        <span className="take-action__organizer-card-label">Campaign organizer</span>
        <span className="take-action__organizer-card-name-row">
          <span className="take-action__organizer-card-name">{displayName}</span>
          {isVerified ? (
            <VerifiedIcon
              sx={{ fontSize: 18, color: VERIFIED_GOLD, flexShrink: 0 }}
              aria-label="Verified"
              titleAccess="Verified"
            />
          ) : null}
        </span>
      </div>
    </>
  );

  const handleFollowClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      onFollowAuthRequired?.();
      return;
    }
    onFollowToggle?.();
  };

  return (
    <div
      className={`take-action__organizer-card ${
        !canViewProfile ? "take-action__organizer-card--static" : ""
      }`}
    >
      {canViewProfile ? (
        <Link
          to={`/profile/${profileId}`}
          className="take-action__organizer-card-main"
          aria-label={`View ${displayName}'s profile`}
        >
          {profileContent}
        </Link>
      ) : (
        <div className="take-action__organizer-card-main">{profileContent}</div>
      )}
      {canFollow ? (
        <button
          type="button"
          className={`take-action__organizer-follow-btn ${
            isFollowing ? "take-action__organizer-follow-btn--following" : ""
          }`}
          onClick={handleFollowClick}
          disabled={followLoading}
          aria-pressed={isFollowing}
        >
          {followLoading ? "..." : isFollowing ? "Following" : "Follow"}
        </button>
      ) : canViewProfile ? (
        <ChevronRightIcon className="take-action__organizer-card-chevron" aria-hidden="true" />
      ) : null}
    </div>
  );
}

export function telHref(phone) {
  const digits = String(phone || "").replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
}

export function getTakeActionBillLabel(targetBill) {
  if (!targetBill) return null;
  if (targetBill.bill_mode === "external") {
    return targetBill?.external?.link_name || targetBill.title || "Bill resource";
  }
  return getCampaignBillTypeLabel(targetBill.type, targetBill.number, targetBill.congress);
}

export function getTakeActionScheduleState(campaign) {
  if (!campaign) return "unknown";
  if (campaign.is_active_now) return "live";
  const now = Date.now();
  const start = campaign.start_datetime ? new Date(campaign.start_datetime).getTime() : null;
  const end = campaign.end_datetime ? new Date(campaign.end_datetime).getTime() : null;
  if (start && now < start) return "scheduled";
  if (end && now > end) return "ended";
  if (campaign.status === "completed") return "ended";
  return "unavailable";
}

/** Build the same target_bill shape the public take-action page expects from create-form draft state. */
export function buildTakeActionBillFromDraft({ billMode, selectedBill, externalBill, billDbId = null }) {
  if (billMode === "external") {
    const linkName = (externalBill?.link_name || "").trim();
    const linkUrl = (externalBill?.link_url || "").trim();
    if (!linkName && !linkUrl) return null;
    return {
      id: null,
      bill_mode: "external",
      title: linkName,
      external: {
        link_name: linkName,
        link_description: (externalBill?.link_description || "").trim(),
        link_url: linkUrl,
      },
    };
  }
  if (!selectedBill) return null;
  return {
    id: billDbId || selectedBill.dbId || selectedBill.db_id || null,
    bill_mode: "in_app",
    type: selectedBill.type,
    number: selectedBill.number,
    congress: selectedBill.congress,
    title: selectedBill.title,
    jurisdiction: selectedBill.jurisdiction || "federal",
  };
}

export function buildTakeActionCampaignFromDraft({
  name = "",
  description = "",
  actionScript = "",
  organizerName = "",
  organizerId = null,
  organizerProfileImage = null,
  organizerProfilePublic = false,
  organizerIsVerified = false,
  billMode = "in_app",
  selectedBill = null,
  externalBill = null,
  billDbId = null,
  additionalLinks = [],
  repSource = "custom_list",
  customTargets = [],
}) {
  return {
    name: (name || "").trim() || "Campaign name",
    description: (description || "").trim(),
    organizer_name: (organizerName || "").trim(),
    organizer_id: organizerId || null,
    organizer_profile_image: organizerProfileImage || null,
    organizer_profile_public: Boolean(organizerProfilePublic),
    organizer_is_verified: Boolean(organizerIsVerified),
    action_script: (actionScript || "").trim(),
    target_bill: buildTakeActionBillFromDraft({ billMode, selectedBill, externalBill, billDbId }),
    additional_links: additionalLinks,
    rep_source: repSource,
    custom_targets: customTargets,
    is_active_now: true,
    status: "active",
    start_datetime: null,
    end_datetime: null,
    action_completions: 0,
  };
}

function isFederalCommentableBill(targetBill) {
  if (!targetBill || targetBill.bill_mode === "external") return false;
  const jurisdiction = String(targetBill.jurisdiction || "federal").toLowerCase();
  const hasBillRef = Boolean(targetBill.id || (targetBill.type && targetBill.number));
  return jurisdiction === "federal" && hasBillRef;
}

function resolveBillHref(targetBill) {
  if (!targetBill || targetBill.bill_mode === "external") return null;
  if (targetBill.id) return `/bills/${targetBill.id}`;
  if (targetBill.type && targetBill.number) {
    return `/bills/${String(targetBill.type).toLowerCase()}-${targetBill.number}`;
  }
  return null;
}

function BillAboutCard({ targetBill }) {
  if (!targetBill) return null;

  const billLabel = getTakeActionBillLabel(targetBill);
  const isExternal = targetBill.bill_mode === "external";
  const externalUrl = (targetBill?.external?.link_url || "").trim();
  const billHref = resolveBillHref(targetBill);
  const isFederalCommentable = isFederalCommentableBill(targetBill);
  const billTitle = isExternal ? targetBill?.external?.link_description : targetBill?.title;
  const isClickable = (isExternal && externalUrl) || Boolean(billHref);

  const ctaText = isExternal
    ? "Open bill link"
    : isFederalCommentable
      ? "See what people are saying"
      : "View bill details";

  const billBody = (
    <>
      <span className="take-action__bill-label">{billLabel}</span>
      {billTitle ? <span className="take-action__bill-title">{billTitle}</span> : null}
      {isClickable ? (
        <>
          <span className="take-action__bill-cta take-action__bill-cta--desktop">
            <span>{ctaText}</span>
            <OpenInNewIcon className="take-action__bill-cta-icon" aria-hidden="true" />
          </span>
          <span className="take-action__bill-read-more" aria-hidden="true">
            <span>Read more</span>
            <ChevronRightIcon className="take-action__bill-read-more-icon" />
          </span>
        </>
      ) : null}
    </>
  );

  return (
    <section className="take-action__section">
      <h2 className="take-action__section-title">About the bill</h2>
      {isExternal && externalUrl ? (
        <a
          href={externalUrl}
          target="_blank"
          rel="noreferrer"
          className="take-action__bill-link"
        >
          {billBody}
        </a>
      ) : billHref ? (
        <a
          href={billHref}
          target="_blank"
          rel="noreferrer"
          className="take-action__bill-link"
        >
          {billBody}
        </a>
      ) : (
        <div className="take-action__bill-body">{billBody}</div>
      )}
    </section>
  );
}

function ScheduledGateCard({ campaign }) {
  const openLabel = campaign?.start_datetime
    ? formatDateWithTimezone(campaign.start_datetime, campaign?.timezone)
    : "";

  return (
    <section className="take-action__section take-action__gate">
      <span className="take-action__gate-badge">Scheduled</span>
      <h2 className="take-action__gate-title">Campaign starting soon</h2>
      <p className="take-action__gate-text">
        {openLabel ? (
          <>
            This campaign opens <strong>{openLabel}</strong>.
          </>
        ) : (
          "This campaign hasn't opened yet."
        )}
      </p>
      <p className="take-action__gate-hint">
        You&apos;re in the right place — this link and QR code are correct. Check back after it
        opens to see the bill, call script, and who to contact.
      </p>
    </section>
  );
}

function RelatedLinkCard({ name, description, url }) {
  const linkUrl = (url || "").trim();
  const content = (
    <>
      <div className="take-action__link-name">{name}</div>
      {description ? <div className="take-action__link-desc">{description}</div> : null}
      {linkUrl ? (
        <p className="take-action__link-url">
          <OpenInNewIcon className="take-action__target-icon" aria-hidden="true" />
          <span>{linkUrl}</span>
        </p>
      ) : null}
    </>
  );

  if (linkUrl) {
    return (
      <a
        href={linkUrl}
        target="_blank"
        rel="noreferrer"
        className="take-action__link-item"
      >
        {content}
      </a>
    );
  }

  return <div className="take-action__link-item take-action__link-item--static">{content}</div>;
}

function CallTargetCard({ name, title, phone, party, url }) {
  const href = telHref(phone);
  const websiteUrl = (url || "").trim();

  return (
    <div className="take-action__target">
      <div className="take-action__target-heading">
        <div className="take-action__target-name">{name}</div>
        {(title || party) && (
          <div className="take-action__target-meta">
            {[title, party].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
      {phone ? (
        <div className="take-action__target-contact">
          {href ? (
            <a className="take-action__target-phone take-action__target-phone--link take-action__target-phone--primary" href={href}>
              <PhoneOutlinedIcon className="take-action__target-icon take-action__target-icon--phone" aria-hidden="true" />
              <span>{phone}</span>
            </a>
          ) : (
            <div className="take-action__target-phone take-action__target-phone--primary">
              <PhoneOutlinedIcon className="take-action__target-icon take-action__target-icon--phone" aria-hidden="true" />
              <span>{phone}</span>
            </div>
          )}
        </div>
      ) : (
        <span className="take-action__no-phone">No phone listed</span>
      )}
      {websiteUrl ? (
        <div className="take-action__target-contact take-action__target-contact--url">
          <a
            href={websiteUrl}
            target="_blank"
            rel="noreferrer"
            className="take-action__target-url"
          >
            <OpenInNewIcon className="take-action__target-icon" aria-hidden="true" />
            <span>{websiteUrl}</span>
          </a>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Shared take-action section renderer for the public page and create-flow preview.
 * `mode="preview"` disables interactive actions while keeping the same layout/copy contract.
 */
export default function TakeActionCampaignContent({
  mode = "live",
  campaign,
  scheduleState,
  displayTargets = [],
  callCompletions = 0,
  emailCompletions = 0,
  isLive = true,
  isLoggedIn = false,
  currentUserId = null,
  organizerIsFollowing = false,
  organizerFollowLoading = false,
  onOrganizerFollowToggle,
  onOrganizerFollowAuthRequired,
  alreadyCompletedCall = false,
  alreadyCompletedEmail = false,
  markingCall = false,
  markingEmail = false,
  copied = false,
  viewerReps = null,
  lookupLoading = false,
  lookupError = "",
  onAddressLookup,
  onClearViewerReps,
  onMarkCallComplete,
  onMarkEmailComplete,
  onCopyScript,
  suppressPreviewBanner = false,
}) {
  const preview = mode === "preview";
  const hasBill = Boolean(campaign?.target_bill);
  const hasLinks =
    Array.isArray(campaign?.additional_links) && campaign.additional_links.length > 0;
  const hasScript = Boolean(campaign?.action_script);

  const isScheduledGate = !preview && scheduleState === "scheduled";

  const endedBanner =
    scheduleState === "ended" ? (
      <p className="take-action__schedule-banner take-action__schedule-banner--ended">
        This action window has closed. You can still review the bill, script, and contacts below.
      </p>
    ) : null;

  const previewBanner =
    preview && !suppressPreviewBanner ? (
      <p className="take-action__schedule-banner take-action__schedule-banner--preview">
        Preview — supporters will see this take-action page when the campaign is live.
      </p>
    ) : null;

  return (
    <>
      <OrganizerCard
        name={campaign?.organizer_name}
        profileId={campaign?.organizer_id}
        profileImageUrl={campaign?.organizer_profile_image}
        isVerified={Boolean(campaign?.organizer_is_verified)}
        preview={preview}
        isLoggedIn={isLoggedIn}
        currentUserId={currentUserId}
        isFollowing={organizerIsFollowing}
        followLoading={organizerFollowLoading}
        onFollowToggle={onOrganizerFollowToggle}
        onFollowAuthRequired={onOrganizerFollowAuthRequired}
      />
      <header className="take-action__header">
        <h1 className="take-action__title">{campaign?.name}</h1>
        {campaign?.description ? (
          <p className="take-action__description">{campaign.description}</p>
        ) : null}
        {previewBanner}
        {!preview && endedBanner}
      </header>

      {isScheduledGate ? (
        <div className="take-action__stack">
          <ScheduledGateCard campaign={campaign} />
          {hasBill ? <BillAboutCard targetBill={campaign.target_bill} /> : null}
        </div>
      ) : (
      <div className="take-action__stack">
        {hasBill ? <BillAboutCard targetBill={campaign.target_bill} /> : null}

        {hasLinks ? (
          <section className="take-action__section">
            <h2 className="take-action__section-title">Related links</h2>
            <div className="take-action__links">
              {campaign.additional_links.map((link) => (
                <RelatedLinkCard
                  key={`${link.name}-${link.url}`}
                  name={link.name}
                  description={link.description}
                  url={link.url}
                />
              ))}
            </div>
          </section>
        ) : null}

        {hasScript ? (
          <section className="take-action__section">
            <div className="take-action__script-header">
              <h2 className="take-action__section-title">What to say</h2>
              {!preview ? (
                <button type="button" className="campaign-btn" onClick={onCopyScript}>
                  {copied ? "Copied" : "Copy script"}
                </button>
              ) : null}
            </div>
            <div className="take-action__script">{campaign.action_script}</div>
          </section>
        ) : null}

        <section className="take-action__section">
          <h2 className="take-action__section-title">Who to call</h2>

        {campaign?.rep_source === "viewer_reps" && !viewerReps ? (
          preview ? (
            <p className="take-action__hint">
              Supporters enter their address to find their representative and senators.
            </p>
          ) : (
            <AddressEntryForm
              hint="Enter your address to find your representative and senators."
              submitLabel="Find my representatives"
              onSubmit={onAddressLookup}
              loading={lookupLoading}
              externalError={lookupError}
              submitAlign="center"
            />
          )
        ) : null}

        {displayTargets.length > 0 ? (
          <div className="take-action__targets">
            {displayTargets.map((t, idx) => (
              <CallTargetCard
                key={`${t.name}-${t.phone}-${idx}`}
                name={t.name}
                title={t.title}
                phone={t.phone}
                party={t.party}
                url={t.url}
              />
            ))}
          </div>
        ) : campaign?.rep_source === "custom_list" ? (
          <p className="take-action__hint">No call targets were provided for this campaign.</p>
        ) : null}

        {!preview && campaign?.rep_source === "viewer_reps" && viewerReps ? (
          <button
            type="button"
            className="campaign-btn"
            style={{ marginTop: 12 }}
            onClick={onClearViewerReps}
          >
            Use a different address
          </button>
        ) : null}
        </section>

        <section className="take-action__section take-action__complete">
        <p className="take-action__completions">
          {callCompletions.toLocaleString()} {callCompletions === 1 ? "call" : "calls"} reported
          {" · "}
          {emailCompletions.toLocaleString()} {emailCompletions === 1 ? "email" : "emails"} reported
        </p>
        {preview ? (
          <div className="take-action__complete-actions">
            <span className="campaign-btn campaign-btn--primary take-action__call-btn--static">I called</span>
            <span className="campaign-btn take-action__call-btn--static">I emailed</span>
          </div>
        ) : isLive ? (
          <div className="take-action__complete-actions">
            <button
              type="button"
              className="campaign-btn campaign-btn--primary"
              onClick={onMarkCallComplete}
              disabled={alreadyCompletedCall || markingCall || displayTargets.length === 0}
            >
              {alreadyCompletedCall ? "Thanks — call logged" : markingCall ? "Saving..." : "I called"}
            </button>
            <button
              type="button"
              className="campaign-btn"
              onClick={onMarkEmailComplete}
              disabled={alreadyCompletedEmail || markingEmail || displayTargets.length === 0}
            >
              {alreadyCompletedEmail ? "Thanks — email logged" : markingEmail ? "Saving..." : "I emailed"}
            </button>
          </div>
        ) : (
          <p className="take-action__hint">
            {scheduleState === "scheduled"
              ? "You can log your call or email once this campaign opens."
              : "This action link has expired and is no longer accepting new completions."}
          </p>
        )}
        {!preview && isLoggedIn ? (
          <p className="take-action__hint take-action__hint--signed-in">
            You&apos;re signed in. This page still shows the organizer&apos;s campaign targets and script — not your personal rep list.
          </p>
        ) : null}
        {!preview && !isLoggedIn ? (
          <p className="take-action__hint" style={{ marginTop: 12 }}>
            Create a free account to track your civic actions and find your reps anytime.
          </p>
        ) : null}
        </section>
      </div>
      )}

      <section className="take-action__ios-promo" aria-label="Download CivixThread for iOS">
        <div className="take-action__ios-promo-copy">
          <h2 className="take-action__ios-promo-title">Take CivixThread with you</h2>
          <p className="take-action__ios-promo-text">
            Download the iOS app to track your calls, follow policy, and find your reps anytime.
          </p>
        </div>
        <a
          href={IOS_APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="take-action__ios-promo-btn"
        >
          <AppleIcon className="take-action__ios-promo-btn-icon" aria-hidden="true" />
          <span>Download for iOS</span>
        </a>
      </section>
    </>
  );
}
